import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { type FC, useState } from "react";
import { OnboardingShell } from "./onboardingShell.js";

interface OnboardingNameFormProps {
  email: string;
  initialFirstName: string;
  initialLastName: string;
  isSaving: boolean;
  errorMessage: string | null;
  onSubmit: (firstName: string, lastName: string) => void;
  onSignOut: () => void;
}

/**
 * Presentational name-only onboarding step: the teammate already belongs to a
 * company (resolved server-side) and just needs to provide their first and last
 * name. All persistence is delegated to `onSubmit`.
 */
export const OnboardingNameForm: FC<OnboardingNameFormProps> = ({
  email,
  initialFirstName,
  initialLastName,
  isSaving,
  errorMessage,
  onSubmit,
  onSignOut,
}) => {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  const onClickSave = () => {
    if (!firstName) setFirstNameError("First name is required");
    if (!lastName) setLastNameError("Last name is required");
    if (!firstName || !lastName) return;

    onSubmit(firstName, lastName);
  };

  return (
    <OnboardingShell onSignOut={onSignOut}>
      <h1 className="mb-2 text-center text-2xl font-semibold text-foreground">
        Welcome to Alpharun
      </h1>
      <p className="text-center text-sm text-gray-600">
        Provide a few details to get started.
      </p>

      <div className="mt-4">
        <Input
          label="Email address"
          value={email}
          readOnly
          isDisabled
          onChange={() => {
            /* noop for disabled input */
          }}
          maxLength={100}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        <Input
          label="First name"
          value={firstName}
          autoComplete="given-name"
          errorDescription={firstNameError}
          onChange={(evt) => {
            setFirstNameError("");
            setFirstName(evt.target.value);
          }}
          maxLength={100}
        />
        <Input
          label="Last name"
          value={lastName}
          autoComplete="family-name"
          errorDescription={lastNameError}
          onChange={(evt) => {
            setLastNameError("");
            setLastName(evt.target.value);
          }}
          maxLength={100}
        />
      </div>

      {errorMessage ? (
        <p className="mt-3 text-center text-sm text-destructive">
          {errorMessage}
        </p>
      ) : null}

      <Button
        className="mx-auto mt-6"
        variant={ButtonVariant.Primary}
        onClick={onClickSave}
        isDisabled={isSaving}
      >
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </OnboardingShell>
  );
};
